import type { TabKey } from '@/components/PatientPageV2/components/PrescribedMedicationsTabsTable/PrescribedMedicationsTabsTable.types';
import type { PrescribedMedicationSchema } from '@montugroup/prescription-contracts';
import { MedicationPrescriptionStatus } from '@montugroup/prescription-contracts';
import { useMemo } from 'react';

export interface UseFilteredPrescribedProductsResult {
  data: PrescribedMedicationSchema[];
}

const FILTER_ALL_ACTIVE: TabKey = 'all-active';

/**
 * Filters prescribed medications based on the given filter type.
 *
 * @param medications - Array of prescribed medications to filter.
 * @param filter - The filter type, either 'all' or 'all-active'.
 * @returns An array of filtered prescribed medications.
 */
export const filterPrescribedMedications = (
  medications: PrescribedMedicationSchema[],
  filter: TabKey
): PrescribedMedicationSchema[] => {
  return medications.filter((medication) => {
    if (filter === FILTER_ALL_ACTIVE) {
      return medication.status === MedicationPrescriptionStatus.Active;
    }
    return true;
  });
};

/**
 * Custom hook to filter prescribed products based on the provided filter type ('all-active' or 'all').
 *
 * @param prescribedProductsData - The prescribed products data to filter.
 * @param filter - The filter type, either 'all-active' or 'all'.
 * @returns An object containing the filtered prescribed medications.
 */
export const useFilteredPrescribedProducts = (
  prescribedProductsData: PrescribedMedicationSchema[],
  filter: TabKey
): UseFilteredPrescribedProductsResult => {
  const prescribedMedications = prescribedProductsData ?? [];

  const filteredMedications = useMemo(() => {
    return filterPrescribedMedications(prescribedMedications, filter);
  }, [prescribedMedications, filter]);

  return { data: filteredMedications };
};
